import { MS_INBOX_COUNTERS_PATH } from '../../constants/AppConstants';
import fetchWithTimeout from '../../util/fetchWithTimeout';
import { msInboxApiUrl, buildMsInboxAuthHeaders } from './msInboxClient';

/**
 * Los contadores de startup se piden desde dos flujos que corren casi juntos:
 * `getUnreadChats` al montar el inbox y `refreshMergedPlatformCounts` cuando
 * termina de cargar `retailer_info`. Compartir la respuesta reciente (y la
 * petición en vuelo) evita el doble GET a `/counters`.
 *
 * Esa respuesta no sirve para reconciliar: `refreshMergedPlatformCounts({ force: true })`
 * y cualquier delta local tiran el snapshot. Si no, `setPlatformCounts` volvería a
 * publicar números viejos encima de un badge que el socket ya movió.
 */
const CACHE_TTL_MS = 5000;

let cacheGeneration = 0;
let requestSeq = 0;
let inFlightRequest = null;
let inFlightGeneration = -1;
let cachedCounters = null;
let cachedAt = 0;
let liveRevision = 0;

async function requestMsInboxCounters() {
  try {
    const url = msInboxApiUrl(MS_INBOX_COUNTERS_PATH);
    const headers = await buildMsInboxAuthHeaders();

    const response = await fetchWithTimeout(url, {
      method: 'GET',
      headers,
      mode: 'cors'
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  } catch {
    return null;
  }
}

function rememberCounters(counters, generation, seq) {
  if (generation !== cacheGeneration || seq !== requestSeq) return counters;
  if (counters !== null) {
    cachedCounters = counters;
    cachedAt = Date.now();
  }
  return counters;
}

/**
 * Un delta local (socket o alta/baja del monolito) dejó obsoleto el último GET.
 * La petición que ya iba en vuelo tampoco puede repoblar el caché.
 */
export function noteMsInboxCountersMutated() {
  liveRevision += 1;
  cacheGeneration += 1;
  cachedCounters = null;
  cachedAt = 0;
  inFlightRequest = null;
}

/** Revisión de deltas locales. Sirve para no publicar un snapshot que ya quedó atrás. */
export function msInboxCountersRevision() {
  return liveRevision;
}

/**
 * GET /inbox/api/v1/counters — contadores agregados del microservicio inbox.
 *
 * @param {Object} [options]
 * @param {boolean} [options.force=false] - Ignora caché y la petición en vuelo, y pide de nuevo.
 * @returns {Promise<Object|null>} Respuesta MS o null si falla (401/red/5xx).
 */
export async function fetchMsInboxCounters({ force = false } = {}) {
  if (!force) {
    const cacheIsFresh = cachedCounters !== null
      && (Date.now() - cachedAt) < CACHE_TTL_MS;
    if (cacheIsFresh) return cachedCounters;
    if (inFlightRequest && inFlightGeneration === cacheGeneration) {
      return inFlightRequest;
    }
  } else {
    cacheGeneration += 1;
    cachedCounters = null;
    cachedAt = 0;
    inFlightRequest = null;
  }

  const generation = cacheGeneration;
  requestSeq += 1;
  const seq = requestSeq;
  const promise = requestMsInboxCounters()
    .then((counters) => rememberCounters(counters, generation, seq))
    .finally(() => {
      if (inFlightRequest === promise) inFlightRequest = null;
    });

  inFlightRequest = promise;
  inFlightGeneration = generation;
  return promise;
}

/**
 * Limpia el caché en memoria. La petición que sigue en vuelo no vuelve a escribirlo:
 * un reset de sesión a mitad del GET no puede dejar los contadores del retailer anterior.
 */
export function resetMsInboxCountersCache() {
  cacheGeneration += 1;
  requestSeq += 1;
  inFlightRequest = null;
  inFlightGeneration = -1;
  cachedCounters = null;
  cachedAt = 0;
  liveRevision = 0;
}
